/**
 * PSV-1888 — cosmetics shop modal (hotkey B).
 *
 * Renders the storefront-ordered cosmetic list (see client/data/shop.ts —
 * the platform Storefront/Catalog decide WHICH items show and in what
 * order; prices and render payloads come from shared/cosmetics.ts keyed
 * by sku). Cards show a color swatch, name, kind and state:
 *   price (green/red by affordability) → click buys (PurchaseCosmetic);
 *   OWNED → click equips (EquipCosmetic);
 *   EQUIPPED → click unequips.
 *
 * Same container-level interaction pattern as ClassPickerModal: one
 * full-screen container captures pointerdown, handler resolves card hits
 * from container-local coordinates, backdrop click closes. Rebuilt from
 * scratch on every show().
 */

import Phaser from 'phaser';

import type { CosmeticDef, CosmeticKind } from '../../../server/shared/cosmetics';

export const SHOP_CARD_W = 168;
export const SHOP_CARD_H = 96;
export const SHOP_CARD_GAP = 12;
export const SHOP_COLS = 4;

const BACKDROP_COLOR = 0x000000;
const BACKDROP_ALPHA = 0.65;
const CARD_BG = 0x101521;
const CARD_BG_ALPHA = 0.95;
const BORDER_BUYABLE = 0x44ff7a;
const BORDER_OWNED = 0x66aaff;
const BORDER_EQUIPPED = 0xffd84a;
const BORDER_DIM = 0x3a4768;
const COLOR_TEXT = '#eeeeee';
const COLOR_TEXT_DIM = '#9aa0b4';
const COLOR_COST_OK = '#44ff7a';
const COLOR_COST_SHORT = '#ff5577';
const COLOR_EQUIPPED = '#ffd84a';
const MODAL_DEPTH = 30;

export interface ShopEntryState {
  def: CosmeticDef;
  owned: boolean;
  equipped: boolean;
}

export type ShopAction =
  | { type: 'buy'; itemId: string }
  | { type: 'equip'; itemId: string }
  | { type: 'unequip'; kind: CosmeticKind };

export type ShopActionHandler = (action: ShopAction) => void;

interface CardHit {
  entry: ShopEntryState;
  x: number;
  y: number;
  clickable: boolean;
}

export class ShopModal {
  private readonly scene: Phaser.Scene;
  private readonly onAction: ShopActionHandler;
  private container: Phaser.GameObjects.Container | null = null;
  private hits: CardHit[] = [];

  constructor(scene: Phaser.Scene, onAction: ShopActionHandler) {
    this.scene = scene;
    this.onAction = onAction;
  }

  get isOpen(): boolean {
    return this.container !== null;
  }

  show(entries: ShopEntryState[], gems: number): void {
    this.hide();
    const cam = this.scene.cameras.main;
    const container = this.scene.add
      .container(0, 0)
      .setScrollFactor(0)
      .setDepth(MODAL_DEPTH);
    this.container = container;

    const backdrop = this.scene.add.graphics();
    backdrop.fillStyle(BACKDROP_COLOR, BACKDROP_ALPHA);
    backdrop.fillRect(0, 0, cam.width, cam.height);
    container.add(backdrop);

    const rows = Math.ceil(entries.length / SHOP_COLS);
    const gridW = SHOP_COLS * SHOP_CARD_W + (SHOP_COLS - 1) * SHOP_CARD_GAP;
    const gridH = rows * SHOP_CARD_H + (rows - 1) * SHOP_CARD_GAP;
    const originX = (cam.width - gridW) / 2;
    const originY = (cam.height - gridH) / 2 + 14;

    const title = this.scene.add
      .text(cam.width / 2, originY - 44, 'S H O P', {
        fontSize: '22px',
        color: COLOR_TEXT,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5);
    const wallet = this.scene.add
      .text(cam.width / 2, originY - 20, `${gems} ◆   ·   click to buy / equip   ·   [B / Esc] close`, {
        fontSize: '13px',
        color: COLOR_TEXT_DIM,
      })
      .setOrigin(0.5, 0.5);
    container.add([title, wallet]);

    this.hits = [];
    entries.forEach((entry, i) => {
      const col = i % SHOP_COLS;
      const row = Math.floor(i / SHOP_COLS);
      const x = originX + col * (SHOP_CARD_W + SHOP_CARD_GAP);
      const y = originY + row * (SHOP_CARD_H + SHOP_CARD_GAP);
      const affordable = gems >= entry.def.priceGems;
      const clickable = entry.owned || affordable;
      this.drawCard(container, entry, x, y, affordable);
      this.hits.push({ entry, x, y, clickable });
    });

    container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, cam.width, cam.height),
      Phaser.Geom.Rectangle.Contains,
    );
    container.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const hit = this.hits.find(
        (h) =>
          pointer.x >= h.x &&
          pointer.x <= h.x + SHOP_CARD_W &&
          pointer.y >= h.y &&
          pointer.y <= h.y + SHOP_CARD_H,
      );
      if (hit === undefined) {
        this.hide();
        return;
      }
      if (!hit.clickable) return;
      const { def } = hit.entry;
      if (!hit.entry.owned) {
        this.onAction({ type: 'buy', itemId: def.id });
      } else if (hit.entry.equipped) {
        this.onAction({ type: 'unequip', kind: def.kind });
      } else {
        this.onAction({ type: 'equip', itemId: def.id });
      }
    });
  }

  hide(): void {
    if (this.container === null) return;
    this.container.destroy(true);
    this.container = null;
    this.hits = [];
  }

  destroy(): void {
    this.hide();
  }

  private drawCard(
    container: Phaser.GameObjects.Container,
    entry: ShopEntryState,
    x: number,
    y: number,
    affordable: boolean,
  ): void {
    const border = entry.equipped
      ? BORDER_EQUIPPED
      : entry.owned
        ? BORDER_OWNED
        : affordable
          ? BORDER_BUYABLE
          : BORDER_DIM;
    const g = this.scene.add.graphics();
    g.fillStyle(CARD_BG, CARD_BG_ALPHA);
    g.fillRoundedRect(x, y, SHOP_CARD_W, SHOP_CARD_H, 8);
    g.lineStyle(2, border, 1);
    g.strokeRoundedRect(x, y, SHOP_CARD_W, SHOP_CARD_H, 8);
    // Color swatch — the render payload preview.
    g.fillStyle(entry.def.color, 1);
    g.fillRoundedRect(x + 10, y + 12, 26, 26, 5);
    container.add(g);

    const dim = !entry.owned && !affordable;
    const name = this.scene.add.text(x + 46, y + 10, entry.def.name, {
      fontSize: '13px',
      color: dim ? COLOR_TEXT_DIM : COLOR_TEXT,
      fontStyle: 'bold',
    });
    const kind = this.scene.add.text(x + 46, y + 28, entry.def.kind.toUpperCase(), {
      fontSize: '10px',
      color: COLOR_TEXT_DIM,
    });
    const stateText = entry.equipped
      ? 'EQUIPPED — click to remove'
      : entry.owned
        ? 'OWNED — click to equip'
        : `${entry.def.priceGems} ◆`;
    const stateColor = entry.equipped
      ? COLOR_EQUIPPED
      : entry.owned
        ? '#66aaff'
        : affordable
          ? COLOR_COST_OK
          : COLOR_COST_SHORT;
    const state = this.scene.add.text(x + 10, y + SHOP_CARD_H - 26, stateText, {
      fontSize: '12px',
      color: stateColor,
    });
    if (dim) {
      name.setAlpha(0.6);
      kind.setAlpha(0.6);
    }
    container.add([name, kind, state]);
  }
}
